import axios from 'axios'

import sdk from '@service/auth-sdk'
import ChunkUploadService from '@/src/service/chunk-upload-service'
import {
  applyTaskToUploadItem,
  extractTaskId,
  isTaskFinished,
  registerTaskSource,
  unregisterTaskSource,
} from '@utils/upload-task-poll'
import { createDatastoreTypeHandler } from '@service/datastore-type'

const POLL_SOURCE_KEY = 'datastore'
const pendingCompleteCallbacks = new Map()

function isFile(value) {
  return typeof File !== 'undefined' && value instanceof File
}

function getFormFiles(data) {
  if (!(data instanceof FormData)) return []
  const files = []
  ;['file', 'files'].forEach((key) => {
    data.getAll(key).forEach((value) => {
      if (isFile(value)) files.push(value)
    })
  })
  return files
}

function cloneFormDataWithoutFiles(data) {
  const next = new FormData()
  if (!(data instanceof FormData)) return next
  for (const [key, value] of data.entries()) {
    if (isFile(value)) continue
    next.append(key, value)
  }
  return next
}

const emptyPdfGeorefPrompt = () => ({
  show: false,
  files: [],
  prefill: {},
})

const DEFAULT_UPLOAD_MESSAGES = {
  processing: 'Đang tạo datastore...',
  fail: 'Tạo datastore thất bại',
  failAfterUpload: 'Tạo datastore sau khi upload thất bại',
  abort: 'Có tệp tải lên thất bại, đã hủy tạo datastore',
}

function mergeUploadMessages(messages) {
  return Object.assign({}, DEFAULT_UPLOAD_MESSAGES, messages || {})
}

let pdfGeorefWaiter = null

function rejectPdfGeorefWaiter() {
  if (!pdfGeorefWaiter) return
  const reject = pdfGeorefWaiter.reject
  pdfGeorefWaiter = null
  reject(new Error('cancelled'))
}

function makeQueueId(index = 0) {
  return `${Date.now()}-${index}-${Math.random()
    .toString(36)
    .slice(2, 8)}`
}

function makeChildItem(file, index) {
  return {
    id: makeQueueId(index),
    name: (file && file.name) || 'Đang tải dữ liệu',
    progress: 0,
    isDone: false,
    isError: false,
    statusMessage: 'Đang tải lên...',
    error_message: '',
    error: null,
    failedChunks: [],
    cancel: null,
  }
}

function makeParentItem({ name, type, children = [] }) {
  const isGroup = children.length > 1
  return {
    id: makeQueueId('parent'),
    name: name || 'Đang tải dữ liệu',
    type: type || '',
    isGroup,
    phase: 'upload',
    children: isGroup ? children : [],
    progress: 0,
    isDone: false,
    isError: false,
    isProcessing: false,
    task_id: null,
    statusMessage: isGroup
      ? `Đang tải file (0/${children.length})...`
      : 'Đang tải lên...',
    error_message: '',
    error: null,
    cancel: null,
  }
}

function bindTaskPoller(state, commit) {
  registerTaskSource(POLL_SOURCE_KEY, {
    getItems: () => Object.values(state.queue || {}),
    onUpdate: (updates) => {
      updates.forEach(({ item, task }) => {
        commit('updateQueue', {
          id: item.id,
          queue: applyTaskToUploadItem(item, task),
        })
        fireCompleteCallback(task)
      })
    },
  })
}

function storeCompleteCallback(taskId, cbWhenComplete, res) {
  if (!cbWhenComplete || taskId == null) return
  const key = String(taskId)
  if (pendingCompleteCallbacks.has(key)) return
  pendingCompleteCallbacks.set(key, { cb: cbWhenComplete, res })
}

function fireCompleteCallback(task) {
  if (!task || !isTaskFinished(task.status)) return
  const key = String(task.id)
  const pending = pendingCompleteCallbacks.get(key)
  if (!pending) return
  pendingCompleteCallbacks.delete(key)
  pending.cb(pending.res)
}

function clearCompleteCallbacks() {
  pendingCompleteCallbacks.clear()
}

function markUploadFromResponse(state, commit, id, res, cbWhenComplete) {
  const current = state.queue[id] || {}
  const taskId = extractTaskId(res)
  if (!taskId) {
    commit('updateQueue', {
      id,
      queue: {
        ...current,
        task_id: null,
        isProcessing: false,
        isDone: true,
        statusMessage: 'Hoàn thành',
      },
    })
    if (cbWhenComplete) {
      cbWhenComplete(res)
    }
    return false
  }
  storeCompleteCallback(taskId, cbWhenComplete, res)
  commit('updateQueue', {
    id,
    queue: {
      ...current,
      task_id: taskId,
      isProcessing: true,
      isDone: false,
      isError: false,
      error_message: '',
      statusMessage: 'Đang xử lý...',
    },
  })
  bindTaskPoller(state, commit)
  return true
}

function markQueueError(commit, state, id, message, error = null) {
  const current = state.queue[id] || {}
  commit('updateQueue', {
    id,
    queue: {
      ...current,
      isDone: true,
      isError: true,
      isProcessing: false,
      error_message: message,
      error,
    },
  })
}

export default {
  namespaced: true,
  state: {
    isShowDialog: false,
    progressFiles: [],
    queue: {},
    configSend: {},
    pdfGeorefPrompt: emptyPdfGeorefPrompt(),
  },
  getters: {
    isShowDialog: (state) => state.isShowDialog,
    progressFiles: (state) => state.progressFiles,
    getProgressFileById: (state) => (id) => state.queue[id],
    pdfGeorefPrompt: (state) => state.pdfGeorefPrompt,
  },
  mutations: {
    showDialog(state) {
      state.isShowDialog = true
    },
    closeDialog(state) {
      unregisterTaskSource(POLL_SOURCE_KEY)
      clearCompleteCallbacks()
      rejectPdfGeorefWaiter()
      state.isShowDialog = false
      state.progressFiles = []
      state.queue = {}
      state.configSend = {}
      state.pdfGeorefPrompt = emptyPdfGeorefPrompt()
    },
    setQueueUpload(state, file) {
      state.progressFiles.push(file)
      state.queue[file.id] = file
    },
    updateQueue(state, { id, queue }) {
      state.progressFiles = state.progressFiles.filter((x) => x.id != id)
      state.queue[id] = queue
      state.progressFiles.push(queue)
    },
    updateChild(state, { parentId, childId, patch }) {
      const parent = state.queue[parentId]
      if (!parent) return
      const children = (parent.children || []).map((child) =>
        child.id === childId ? Object.assign({}, child, patch) : child
      )
      const uploadedCount = children.filter(
        (child) => !child.isError && (child.progress >= 100 || child.isDone)
      ).length
      const errorCount = children.filter((child) => child.isError).length
      const progress = children.length
        ? Math.round(
            children.reduce(
              (sum, child) => sum + (Number(child.progress) || 0),
              0
            ) / children.length
          )
        : 0
      const next = Object.assign({}, parent, {
        children,
        progress,
      })
      if (next.phase === 'upload') {
        next.statusMessage = errorCount
          ? `Tải file thất bại (${errorCount}/${children.length})`
          : `Đang tải file (${uploadedCount}/${children.length})...`
      }
      state.progressFiles = state.progressFiles.filter((x) => x.id != parentId)
      state.queue[parentId] = next
      state.progressFiles.push(next)
    },
    removeQueueUpload(state, id) {
      delete state.queue[id]
      state.progressFiles = state.progressFiles.filter((x) => x.id != id)
      if (!state.queue) {
        state.queue = {}
      }
      const hasPendingTask = Object.values(state.queue).some(
        (item) => item && item.task_id && !item.isDone
      )
      if (!hasPendingTask) {
        unregisterTaskSource(POLL_SOURCE_KEY)
      }
    },
    setConfigSend(state, { id, config }) {
      state.configSend[id] = config
    },
    setPdfGeorefPrompt(state, prompt) {
      state.pdfGeorefPrompt = Object.assign(emptyPdfGeorefPrompt(), prompt || {})
    },
  },
  actions: {
    async callUpload(
      { commit, dispatch, state },
      { url, data, option = {}, messages, cbWhenComplete }
    ) {
      const uploadMessages = mergeUploadMessages(messages)
      const files = getFormFiles(data)
      commit('showDialog')

      if (!files.length) {
        return dispatch('uploadDirect', {
          url,
          data,
          option,
          cbWhenComplete,
        })
      }

      const isGroup = files.length > 1
      const children = isGroup
        ? files.map((file, index) => makeChildItem(file, index))
        : []
      const parent = makeParentItem({
        name: data.get('name') || '',
        type: data.get('type') || '',
        children,
      })
      parent.cancel = () => {
        const current = state.queue[parent.id]
        const list =
          current && current.children && current.children.length
            ? current.children
            : children
        if (list.length) {
          list.forEach((child) => {
            if (child && child.cancel) child.cancel()
          })
          return
        }
        if (current && current.cancel && current.cancel !== parent.cancel) {
          current.cancel()
        }
      }
      commit('setQueueUpload', parent)

      const uploaded = await Promise.all(
        files.map((file, index) =>
          dispatch('uploadFile', {
            file,
            parentId: parent.id,
            childId: isGroup ? children[index].id : null,
            messages: uploadMessages,
          })
        )
      )

      if (!state.isShowDialog) {
        return null
      }

      const failed = uploaded.some((item) => !item || !item.file_path)
      if (failed) {
        commit('updateQueue', {
          id: parent.id,
          queue: {
            ...state.queue[parent.id],
            phase: 'upload',
            isDone: true,
            isError: true,
            error_message: uploadMessages.abort,
            statusMessage: 'Tải file thất bại',
          },
        })
        return null
      }

      let filesForCreate = uploaded
      const handler = createDatastoreTypeHandler(data.get('type'))
      try {
        filesForCreate = await handler.afterUpload({
          uploaded,
          data,
          promptGeoref: (payload) => dispatch('promptPdfGeoref', payload),
          onStatus: (phase, statusMessage) => {
            commit('updateQueue', {
              id: parent.id,
              queue: {
                ...(state.queue[parent.id] || {}),
                phase,
                statusMessage,
              },
            })
          },
        })
      } catch (error) {
        if (!state.queue[parent.id]) {
          return null
        }
        markQueueError(
          commit,
          state,
          parent.id,
          error && error.message === 'cancelled'
            ? 'Đã hủy vì chưa nhập tọa độ cho file PDF thường'
            : (error && error.message) ||
                'Không thể kiểm tra tọa độ file PDF',
          error
        )
        return null
      }
      if (!filesForCreate) {
        return null
      }

      return dispatch('createDataset', {
        url,
        data,
        uploaded: filesForCreate,
        parentId: parent.id,
        option,
        messages: uploadMessages,
        cbWhenComplete,
      })
    },
    async callRasterChunkUpload(
      { dispatch },
      { files, payload = {}, cbWhenComplete }
    ) {
      const formData = new FormData()
      Object.keys(payload).forEach((key) => {
        if (payload[key] !== undefined && payload[key] !== null) {
          formData.append(key, payload[key])
        }
      })
      const list = Array.isArray(files) ? files : files ? [files] : []
      list.forEach((file) => {
        if (file) formData.append('file', file)
      })
      return dispatch('callUpload', {
        url: 'datastores',
        data: formData,
        cbWhenComplete,
      })
    },
    async uploadFile({ commit, state }, { file, parentId, childId, messages }) {
      const uploadMessages = mergeUploadMessages(messages)
      const syncItem = (patch = {}) => {
        if (childId) {
          commit('updateChild', {
            parentId,
            childId,
            patch,
          })
          return
        }
        const current = state.queue[parentId] || {}
        commit('updateQueue', {
          id: parentId,
          queue: {
            ...current,
            ...patch,
          },
        })
      }

      const serviceState = childId
        ? ((state.queue[parentId] && state.queue[parentId].children) || []).find(
            (item) => item.id === childId
          ) || {}
        : { ...(state.queue[parentId] || {}) }

      return new Promise((resolve) => {
        const uploadService = new ChunkUploadService({
          state: serviceState,
          onComplete: async (chunkData) => {
            syncItem({
              progress: 100,
              isDone: childId ? true : false,
              isError: false,
              statusMessage: childId ? 'Đã tải lên' : uploadMessages.processing,
            })
            resolve({
              id: childId || parentId,
              file_path: chunkData && chunkData.path,
              file_name: file && file.name ? file.name : '',
            })
          },
          onProgress: (uploadState) => {
            syncItem(uploadState)
          },
          onError: (message) => {
            syncItem({
              isDone: true,
              isError: true,
              error_message: message || 'Tập tin tải lên lỗi',
              statusMessage: message || 'Tập tin tải lên lỗi',
            })
            resolve(null)
          },
        })

        syncItem({
          cancel: () => uploadService.cancelUpload(),
        })
        uploadService.startUpload(file)
      })
    },
    async createDataset(
      { commit, state },
      { url, data, uploaded = [], parentId, option = {}, messages, cbWhenComplete }
    ) {
      const uploadMessages = mergeUploadMessages(messages)
      const formData = cloneFormDataWithoutFiles(data)
      const handler = createDatastoreTypeHandler(data.get('type'))
      const meta = handler.parseUploadMeta(data)
      uploaded.forEach((item, index) => {
        handler.appendUploadedFile(formData, index, item, meta)
      })
      if (uploaded.length === 1) {
        formData.append('file_path', uploaded[0].file_path)
        formData.append('file_name', uploaded[0].file_name)
      }

      const parent = state.queue[parentId] || {}
      commit('updateQueue', {
        id: parentId,
        queue: {
          ...parent,
          phase: 'create',
          progress: 100,
          statusMessage: uploadMessages.processing,
        },
      })

      try {
        const res = await sdk.request({
          method: 'post',
          url,
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          ...option,
        })

        markUploadFromResponse(state, commit, parentId, res, cbWhenComplete)
        const current = state.queue[parentId]
        if (current) {
          commit('updateQueue', {
            id: parentId,
            queue: {
              ...current,
              phase: current.task_id ? 'task' : 'done',
            },
          })
        }

        return res
      } catch (error) {
        const serverMessage =
          error &&
          error.response &&
          error.response.data &&
          (error.response.data.message ||
            (error.response.data.errors &&
              error.response.data.errors.files &&
              error.response.data.errors.files[0]))
        markQueueError(
          commit,
          state,
          parentId,
          serverMessage || uploadMessages.failAfterUpload,
          error
        )
        const current = state.queue[parentId]
        if (current) {
          commit('updateQueue', {
            id: parentId,
            queue: {
              ...current,
              phase: 'create',
              statusMessage: uploadMessages.fail,
            },
          })
        }
        return null
      }
    },
    promptPdfGeoref({ commit }, { files, prefill }) {
      return new Promise((resolve, reject) => {
        rejectPdfGeorefWaiter()
        pdfGeorefWaiter = { resolve, reject }
        commit('setPdfGeorefPrompt', {
          show: true,
          files: files || [],
          prefill: prefill || {},
        })
      })
    },
    resolvePdfGeoref({ commit }, georefs) {
      const waiter = pdfGeorefWaiter
      pdfGeorefWaiter = null
      commit('setPdfGeorefPrompt', emptyPdfGeorefPrompt())
      if (waiter) waiter.resolve(georefs || {})
    },
    cancelPdfGeoref({ commit }) {
      commit('setPdfGeorefPrompt', emptyPdfGeorefPrompt())
      rejectPdfGeorefWaiter()
    },
    async uploadDirect(
      { commit, dispatch },
      { url, data, option = {}, cbWhenComplete }
    ) {
      const id = makeQueueId()
      const CancelToken = axios.CancelToken
      const source = CancelToken.source()
      const file_temp = {
        id,
        name: data.get('name') || 'Đang tải dữ liệu',
        type: data.get('type') || '',
        progress: 0,
        isDone: false,
        isError: false,
        isProcessing: false,
        task_id: null,
        statusMessage: '',
        error_message: '',
        error: null,
        cancel: () => source.cancel('Hủy tải'),
      }

      const { headers = {}, ...restOption } = option
      const config = {
        method: 'post',
        url,
        data,
        headers: {
          'Content-Type': 'multipart/form-data',
          ...headers,
        },
        cancelToken: source.token,
        cbWhenComplete,
        onUploadProgress: (progressEvent) => {
          if (progressEvent.total) {
            const progress = Math.round(
              (progressEvent.loaded * 100) / progressEvent.total
            )
            commit('updateQueue', {
              id,
              queue: {
                ...file_temp,
                progress,
              },
            })
          }
          if (typeof restOption.onUploadProgress === 'function') {
            restOption.onUploadProgress(progressEvent)
          }
        },
        ...restOption,
      }

      commit('setConfigSend', { id, config })
      commit('setQueueUpload', file_temp)
      dispatch('upload', { id })

      return { id, queue: file_temp }
    },
    async cancelUpload({ state, commit }, { id }) {
      const file = state.queue[id]
      if (!file) return
      if (file.task_id && !file.isDone) {
        commit('removeQueueUpload', id)
        return
      }
      if (!file.isDone && file.cancel) {
        file.cancel()
      }
    },
    async cancelAll({ state, commit }) {
      unregisterTaskSource(POLL_SOURCE_KEY)
      clearCompleteCallbacks()
      rejectPdfGeorefWaiter()
      commit('setPdfGeorefPrompt', emptyPdfGeorefPrompt())
      for (const queueID in state.queue) {
        const file = state.queue[queueID]
        if (!file) continue
        if (!file.isDone && file.cancel) {
          file.cancel()
        }
        let temp = file.children || []
        temp.forEach((child) => {
          if (child && !child.isDone && child.cancel) {
            child.cancel()
          }
        })
      }
      state.queue = {}
      state.isShowDialog = false
      state.progressFiles = []
    },
    async reUpload({ dispatch, commit, state }, { id }) {
      const oldConfig = state.configSend[id]
      const oldFile_temp = state.progressFiles.find((x) => x.id == id)
      const CancelToken = axios.CancelToken
      const source = CancelToken.source()
      const config = {
        ...oldConfig,
        cancelToken: source.token,
      }
      const file_temp = {
        ...oldFile_temp,
        isDone: false,
        isError: false,
        isProcessing: false,
        task_id: null,
        statusMessage: '',
        error_message: '',
        cancel: () => source.cancel(),
      }
      commit('setConfigSend', { id, config })
      commit('setQueueUpload', file_temp)
      commit('updateQueue', { id, queue: file_temp })
      dispatch('upload', { id })
    },
    async upload({ state, commit }, { id }) {
      const config = state.configSend[id]
      const current = () => state.queue[id] || {}
      try {
        const res = await sdk.request(config)
        markUploadFromResponse(state, commit, id, res, config.cbWhenComplete)
      } catch (error) {
        const file_temp = current()
        file_temp.isError = true
        file_temp.isDone = true
        file_temp.isProcessing = false
        file_temp.task_id = null
        if (!axios.isCancel(error) || error.code != 'ECONNABORTED') {
          if (
            error.response &&
            error.response.status == 400 &&
            error.response.data &&
            error.response.data.message
          ) {
            file_temp.error_message = error.response.data.message
          } else file_temp.error_message = 'Tập tin tải lên lỗi'
          file_temp.error = error
        } else {
          file_temp.error_message = 'Hủy tải'
        }
        commit('updateQueue', { id, queue: { ...file_temp } })
      }
    },
  },
}
